import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  EditIcon,
  AddIcon,
  CheckCircleIcon,
  WarningIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { FaChargingStation, FaBolt, FaPlug, FaHistory } from "react-icons/fa";

import {
  PAYOUT_CONFIG_FIELDS,
  PAYOUT_CONFIG_GROUPS,
} from "../variables/payoutConfigFields";

const MotionTr = motion(Tr);
const MotionBox = motion(Box);

// PaginationButton — mirrors LocationsTable
const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "orange" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

// GlassMorphismCard wrapper — mirrors LocationsTable
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)",
  );
  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

// Charger avatar — mirrors LocationAvatar
const ChargerAvatar = ({ name }) => {
  const initials = (name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
  return (
    <Avatar
      name={name}
      size="sm"
      bg="#f57d36"
      color="white"
      fontWeight="bold"
      getInitials={() => initials}
    />
  );
};

// Status badge for a charger's status
const StatusBadge = ({ status }) => {
  const s = status?.toLowerCase();
  let color, icon, label;
  if (s === "available" || s === "online") {
    color = "green";
    icon = CheckCircleIcon;
    label = status || "Available";
  } else if (s === "charging" || s === "occupied") {
    color = "blue";
    icon = CheckCircleIcon;
    label = status || "Charging";
  } else if (s === "faulted" || s === "unavailable") {
    color = "red";
    icon = WarningIcon;
    label = status || "Faulted";
  } else {
    color = "gray";
    icon = WarningIcon;
    label = status || "Unknown";
  }
  return (
    <Badge
      display="flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
    >
      <Icon as={icon} mr={1} boxSize={3} />
      <Text fontSize="xs" fontWeight="medium" textTransform="capitalize">
        {label}
      </Text>
    </Badge>
  );
};

// Type badge (AC / DC) — mirrors LocationsTable badge style
const TypeBadge = ({ powerType }) => {
  const isDC = powerType?.toUpperCase() === "DC";
  return (
    <Badge
      colorScheme={isDC ? "orange" : "blue"}
      variant="subtle"
      borderRadius="md"
      px={2}
      display="flex"
      alignItems="center"
      gap={1}
    >
      <Icon as={isDC ? FaBolt : FaPlug} boxSize={3} />
      <Text fontSize="xs" fontWeight="medium">
        {powerType || "—"}
      </Text>
    </Badge>
  );
};

// Config badge — mirrors StatusBadge style
const ConfigBadge = ({ state }) => {
  if (state === "configured") {
    return (
      <Badge
        display="flex"
        alignItems="center"
        px={3}
        py={1}
        borderRadius="full"
        colorScheme="green"
        variant="subtle"
      >
        <Icon as={CheckCircleIcon} mr={1} boxSize={3} />
        <Text fontSize="xs" fontWeight="medium">
          Configured
        </Text>
      </Badge>
    );
  }
  return (
    <Badge
      display="flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      colorScheme="red"
      variant="subtle"
    >
      <Icon as={WarningIcon} mr={1} boxSize={3} />
      <Text fontSize="xs" fontWeight="medium">
        Not Set
      </Text>
    </Badge>
  );
};

const formatValue = (field, value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const num = Number(value).toFixed(2);
  if (field.unit === "%") return `${num}%`;
  if (field.unit === "₹") return `₹${num}`;
  return num;
};

// Right pane: table of chargers for the selected tenant.
// Design mirrors LocationsTable — GlassMorphismCard, header with search
// on the left + title/count on the right, clean table with avatar + badges.
const PayoutConfigChargersTable = ({
  tenant,
  chargers,
  loading,
  expandedRowId,
  onToggleRow,
  onEdit,
}) => {
  const textColor = useColorModeValue("gray.700", "white");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const theadBgColor = useColorModeValue("gray.50", "gray.800");
  const expandedRowBg = useColorModeValue(
    "rgba(245, 125, 54, 0.04)",
    "whiteAlpha.100",
  );
  const summaryBg = useColorModeValue(
    "rgba(245, 125, 54, 0.03)",
    "whiteAlpha.50",
  );
  const fieldCardBg = useColorModeValue("white", "whiteAlpha.100");
  const groupHeadingColor = useColorModeValue("#f57d36", "orange.300");
  const inputBg = useColorModeValue("white", "whiteAlpha.100");
  const themeColor = "#f57d36";
  const navigate = useNavigate();

  const [configFilter, setConfigFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);

  const filteredChargers = useMemo(() => {
    let list = chargers;
    if (configFilter === "configured") {
      list = list.filter((c) => c.ChargerPayoutConfig);
    } else if (configFilter === "not_configured") {
      list = list.filter((c) => !c.ChargerPayoutConfig);
    }
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) =>
          c.LocationName?.toLowerCase().includes(q) ||
          c.StationId?.toLowerCase().includes(q) ||
          c.Id?.toString().toLowerCase().includes(q),
      );
    }
    return list;
  }, [chargers, configFilter, searchQuery]);

  // Reset to first page when filters/search/tenant change
  useEffect(() => {
    setPageIndex(0);
  }, [configFilter, searchQuery, chargers]);

  const pageCount = Math.max(1, Math.ceil(filteredChargers.length / pageSize));
  const currentPage = Math.min(pageIndex, pageCount - 1);
  const paginatedChargers = filteredChargers.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize,
  );

  return (
    <GlassMorphismCard flex={1} display="flex" flexDirection="column">
      {/* Header with search — mirrors LocationsTable layout */}
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
        >
          {/* Search + filter on left */}
          <VStack align="start" spacing={2} width={{ base: "100%", md: "auto" }}>
            <HStack spacing={2} w="100%">
              <InputGroup size="sm" w={{ base: "100%", md: "300px" }}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by location, station ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderRadius="md"
                  bg={inputBg}
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                />
              </InputGroup>
              <Select
                size="sm"
                borderRadius="md"
                value={configFilter}
                onChange={(e) => setConfigFilter(e.target.value)}
                bg={inputBg}
                maxW="160px"
                _focus={{
                  borderColor: themeColor,
                  boxShadow: `0 0 0 1px ${themeColor}`,
                }}
              >
                <option value="all">All Configs</option>
                <option value="configured">Configured</option>
                <option value="not_configured">Not Set</option>
              </Select>
            </HStack>
          </VStack>

          {/* Title on right */}
          <Flex align="center" justify="flex-end">
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Chargers
            </Text>
          </Flex>
        </Flex>
      </Box>

      {/* Table */}
      {loading && chargers.length === 0 ? (
        <Flex justify="center" align="center" py={16}>
          <Spinner color={themeColor} size="xl" thickness="4px" />
        </Flex>
      ) : chargers.length === 0 ? (
        <Flex direction="column" align="center" justify="center" py={12} px={4}>
          <Icon as={FaChargingStation} boxSize={16} color={themeColor} mb={4} />
          <Text fontSize="xl" fontWeight="bold" mb={2}>
            No Chargers
          </Text>
          <Text color={subTextColor} textAlign="center" maxW="md">
            This tenant doesn't have any chargers yet. New chargers will appear
            here once added.
          </Text>
        </Flex>
      ) : filteredChargers.length === 0 ? (
        <Flex direction="column" align="center" justify="center" py={12} px={4}>
          <Icon as={FaChargingStation} boxSize={16} color={themeColor} mb={4} />
          <Text fontSize="xl" fontWeight="bold" mb={2}>
            No Matching Chargers
          </Text>
          <Text color={subTextColor} textAlign="center" maxW="md">
            Try changing your search or config filter.
          </Text>
        </Flex>
      ) : (
        <>
        <Box flex={1} display="flex" flexDirection="column">
        <TableContainer flex={1}>
          <Table variant="simple" colorScheme="gray">
            <Thead bg={theadBgColor}>
              <Tr>
                <Th
                  borderColor={borderColor}
                  py={4}
                  px={6}
                  w="40px"
                  textTransform="uppercase"
                  fontSize="xs"
                  fontWeight="bold"
                  letterSpacing="wider"
                  color="gray.500"
                />
                {["Location", "Type", "Power", "Status", "Payout Config", "Actions"].map(
                  (h, i) => (
                    <Th
                      key={h}
                      borderColor={borderColor}
                      py={4}
                      px={6}
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      color="gray.500"
                      textAlign={i === 0 ? "left" : "center"}
                    >
                      {h}
                    </Th>
                  ),
                )}
              </Tr>
            </Thead>
            <Tbody>
              {paginatedChargers.map((station) => {
                const isExpanded = expandedRowId === station.Id;
                const config = station.ChargerPayoutConfig || null;
                const configState = config
                  ? "configured"
                  : "not_configured";

                return (
                  <React.Fragment key={station.Id}>
                    <MotionTr
                      whileHover={{ backgroundColor: hoverBg }}
                      transition={{ duration: 0.2 }}
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                      onClick={() => onToggleRow(station)}
                      bg={isExpanded ? expandedRowBg : "transparent"}
                    >
                      {/* Expand toggle */}
                      <Td borderColor={borderColor} py={4} px={6}>
                        <Flex align="center" justify="center">
                          <Icon
                            as={
                              isExpanded ? ChevronDownIcon : ChevronRightIcon
                            }
                            color={isExpanded ? themeColor : "gray.400"}
                            boxSize={4}
                          />
                        </Flex>
                      </Td>
                      {/* Location with avatar */}
                      <Td borderColor={borderColor} py={4} px={6} fontSize="sm">
                        <Flex align="center" gap={3}>
                          <ChargerAvatar name={station.LocationName || "?"} />
                          <Box>
                            <Text color={textColor} fontWeight="semibold">
                              {station.LocationName || "—"}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {station.StationId || station.Id}
                            </Text>
                          </Box>
                        </Flex>
                      </Td>
                      {/* Type */}
                      <Td
                        borderColor={borderColor}
                        py={4}
                        px={6}
                        textAlign="center"
                      >
                        <Flex align="center" justify="center">
                          <TypeBadge powerType={station.PowerType} />
                        </Flex>
                      </Td>
                      {/* Power */}
                      <Td
                        borderColor={borderColor}
                        py={4}
                        px={6}
                        textAlign="center"
                      >
                        <Text color={textColor} fontSize="sm" fontWeight="medium">
                          {station.MaxElectricPower
                            ? `${Number(station.MaxElectricPower).toFixed(1)} kW`
                            : "N/A"}
                        </Text>
                      </Td>
                      {/* Status */}
                      <Td
                        borderColor={borderColor}
                        py={4}
                        px={6}
                        textAlign="center"
                      >
                        <Flex align="center" justify="center">
                          <StatusBadge status={station.Status} />
                        </Flex>
                      </Td>
                      {/* Payout Config */}
                      <Td
                        borderColor={borderColor}
                        py={4}
                        px={6}
                        textAlign="center"
                      >
                        <Flex align="center" justify="center">
                          <ConfigBadge state={configState} />
                        </Flex>
                      </Td>
                      {/* Actions */}
                      <Td
                        borderColor={borderColor}
                        py={4}
                        px={6}
                        textAlign="center"
                      >
                        <Flex align="center" justify="center">
                          <HStack
                            as="button"
                            spacing={1}
                            color={textColor}
                            fontSize="xs"
                            fontWeight="medium"
                            textDecor="underline"
                            cursor="pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/payouts/configuration/history/${station.Id}`);
                            }}
                            _hover={{ color: themeColor }}
                          >
                            <Icon as={FaHistory} boxSize={3} />
                            <Text>View History</Text>
                          </HStack>
                        </Flex>
                      </Td>
                    </MotionTr>

                    {/* Expanded config row */}
                    {isExpanded && (
                      <Tr>
                        <Td
                          colSpan={7}
                          p={0}
                          borderColor={borderColor}
                          bg={summaryBg}
                        >
                          <MotionBox
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            transition={{ duration: 0.2 }}
                            px={6}
                            py={5}
                          >
                            {config ? (
                              <Box>
                                <Flex
                                  justify="space-between"
                                  align="center"
                                  mb={4}
                                  wrap="wrap"
                                  gap={2}
                                >
                                  <HStack spacing={2}>
                                    <Text
                                      fontSize="sm"
                                      fontWeight="bold"
                                      color={textColor}
                                    >
                                      Payout Configuration
                                    </Text>
                                    {config.Month && config.Year && (
                                      <Badge
                                        colorScheme="gray"
                                        variant="subtle"
                                        borderRadius="full"
                                        fontSize="10px"
                                        px={2}
                                      >
                                        {config.Month}/{config.Year}
                                      </Badge>
                                    )}
                                  </HStack>
                                  <Button
                                    leftIcon={<EditIcon />}
                                    bg={themeColor}
                                    color="white"
                                    _hover={{
                                      bg: "#e06826",
                                      transform: "translateY(-1px)",
                                      boxShadow: "0 4px 12px rgba(245, 125, 54, 0.3)",
                                    }}
                                    size="sm"
                                    borderRadius="lg"
                                    onClick={() => onEdit(station, config)}
                                  >
                                    Edit
                                  </Button>
                                </Flex>

                                <VStack align="stretch" spacing={4}>
                                  {PAYOUT_CONFIG_GROUPS.map((group) => {
                                    const groupFields =
                                      PAYOUT_CONFIG_FIELDS.filter(
                                        (f) => f.group === group,
                                      );
                                    return (
                                      <Box key={group}>
                                        <Text
                                          fontSize="10px"
                                          fontWeight="bold"
                                          textTransform="uppercase"
                                          letterSpacing="wider"
                                          color={groupHeadingColor}
                                          mb={2}
                                        >
                                          {group}
                                        </Text>
                                        <SimpleGrid
                                          columns={{ base: 2, md: 4 }}
                                          spacing={3}
                                        >
                                          {groupFields.map((field) => (
                                            <Box
                                              key={field.name}
                                              p={3}
                                              borderRadius="xl"
                                              bg={fieldCardBg}
                                              border="1px solid"
                                              borderColor={borderColor}
                                            >
                                              <Text
                                                fontSize="11px"
                                                color={subTextColor}
                                                fontWeight="medium"
                                                mb={1}
                                                noOfLines={1}
                                              >
                                                {field.shortLabel}
                                              </Text>
                                              <Text
                                                fontSize="md"
                                                fontWeight="bold"
                                                color={textColor}
                                              >
                                                {formatValue(
                                                  field,
                                                  config[field.name],
                                                )}
                                              </Text>
                                            </Box>
                                          ))}
                                        </SimpleGrid>
                                      </Box>
                                    );
                                  })}
                                </VStack>
                              </Box>
                            ) : (
                              <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                py={6}
                                gap={3}
                              >
                                <Flex
                                  align="center"
                                  justify="center"
                                  w="48px"
                                  h="48px"
                                  borderRadius="full"
                                  bg="rgba(245, 125, 54, 0.12)"
                                >
                                  <Icon
                                    as={WarningIcon}
                                    boxSize={6}
                                    color={themeColor}
                                  />
                                </Flex>
                                <Text
                                  fontSize="sm"
                                  color={subTextColor}
                                  textAlign="center"
                                >
                                  No payout configuration set for this charger.
                                </Text>
                                <Button
                                  leftIcon={<AddIcon />}
                                  bg={themeColor}
                                  color="white"
                                  _hover={{
                                    bg: "#e06826",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 4px 12px rgba(245, 125, 54, 0.3)",
                                  }}
                                  size="sm"
                                  borderRadius="lg"
                                  onClick={() => onEdit(station, null)}
                                >
                                  Create Configuration
                                </Button>
                              </Flex>
                            )}
                          </MotionBox>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
        </Box>

        {/* Pagination — mirrors LocationsTable */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageIndex(0);
              }}
              size="sm"
              borderRadius="md"
              w="120px"
              bg={inputBg}
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {paginatedChargers.length} of {filteredChargers.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={currentPage === 0}
              onClick={() => setPageIndex(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={currentPage === 0}
              onClick={() => setPageIndex(currentPage - 1)}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={currentPage === i}
                  onClick={() => setPageIndex(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              <>
                {currentPage > 1 && (
                  <PaginationButton onClick={() => setPageIndex(0)}>
                    1
                  </PaginationButton>
                )}
                {currentPage > 2 && <Text>...</Text>}
                {currentPage > 0 && (
                  <PaginationButton onClick={() => setPageIndex(currentPage - 1)}>
                    {currentPage}
                  </PaginationButton>
                )}
                <PaginationButton isActive onClick={() => setPageIndex(currentPage)}>
                  {currentPage + 1}
                </PaginationButton>
                {currentPage < pageCount - 1 && (
                  <PaginationButton onClick={() => setPageIndex(currentPage + 1)}>
                    {currentPage + 2}
                  </PaginationButton>
                )}
                {currentPage < pageCount - 3 && <Text>...</Text>}
                {currentPage < pageCount - 2 && (
                  <PaginationButton onClick={() => setPageIndex(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={currentPage >= pageCount - 1}
              onClick={() => setPageIndex(currentPage + 1)}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={currentPage >= pageCount - 1}
              onClick={() => setPageIndex(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>
        </>
      )}
    </GlassMorphismCard>
  );
};

export default PayoutConfigChargersTable;
